import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type BillingPeriod = "monthly" | "6m" | "yearly";

export interface RegisterPlan {
  id: string;
  name: string;
  points: number;
  prices: { monthly: number; "6m": number; yearly: number };
  features?: string[];
}

interface RegisterContextValue {
  billingPeriod: BillingPeriod;
  setBillingPeriod: (period: BillingPeriod) => void;
  selectedPlan: RegisterPlan | null;
  setSelectedPlan: (plan: RegisterPlan | null) => void;
  clear: () => void;
}

const RegisterContext = createContext<RegisterContextValue | undefined>(
  undefined
);

const STORAGE_KEY = "toddlar_register_state";

export const RegisterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");
  const [selectedPlan, setSelectedPlan] = useState<RegisterPlan | null>(null);

  // Load persisted state
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<{
        billingPeriod: BillingPeriod;
        selectedPlan: RegisterPlan | null;
      }>;
      if (parsed.billingPeriod) setBillingPeriod(parsed.billingPeriod);
      if (parsed.selectedPlan) setSelectedPlan(parsed.selectedPlan);
    } catch {}
  }, []);

  // Persist on change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ billingPeriod, selectedPlan })
      );
    } catch {}
  }, [billingPeriod, selectedPlan]);

  const clear = () => {
    setBillingPeriod("monthly");
    setSelectedPlan(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  };

  const value = useMemo<RegisterContextValue>(
    () => ({
      billingPeriod,
      setBillingPeriod,
      selectedPlan,
      setSelectedPlan,
      clear,
    }),
    [billingPeriod, selectedPlan]
  );

  return (
    <RegisterContext.Provider value={value}>
      {children}
    </RegisterContext.Provider>
  );
};

export const useRegister = () => {
  const ctx = useContext(RegisterContext);
  if (!ctx) throw new Error("useRegister must be used within RegisterProvider");
  return ctx;
};
